import { unzipSync } from "fflate";
import type { CapabilityFile, CapabilitySkill } from "@agentsaas/protocol";
import { toSkillName, validPath } from "./model";

/**
 * 浏览器端把"上传的文件夹 / zip"解析成 skill 的 files[]（CapabilitySkill）：
 *   - 每个 SKILL.md 所在目录是一个 skill 根；文件归属于路径前缀最长的根
 *   - 根目录名就是 skill 名（规整成 [a-z0-9-]）；根就是压缩包 / 文件夹本身时用 fallbackName
 *   - 找不到任何 SKILL.md：整体当成一个 skill（去掉唯一的公共顶层目录），由用户补 SKILL.md
 *   - 文本（合法 UTF-8 且不含 NUL）按 utf8 存，其余 base64；以 #! 开头的文件标记可执行
 *   - 忽略 __MACOSX/、.DS_Store、Thumbs.db 与空目录项
 */

export interface RawEntry {
  path: string;
  data: Uint8Array;
}

export interface ImportResult {
  skills: CapabilitySkill[];
  /** 被忽略的文件（不在任何 skill 根下、路径非法） */
  skipped: string[];
  /** 没有 SKILL.md 的 skill 名 */
  missingSkillMd: string[];
}

const IGNORED = (p: string) => p.split("/").some((s) => s === "__MACOSX" || s === ".DS_Store" || s === "Thumbs.db" || s === ".git");

function normalize(p: string): string {
  return p.replace(/\\/g, "/").replace(/^\.\//, "").replace(/^\/+/, "");
}

function toBase64(bytes: Uint8Array): string {
  let s = "";
  const chunk = 0x8000;
  for (let i = 0; i < bytes.length; i += chunk) s += String.fromCharCode(...bytes.subarray(i, i + chunk));
  return btoa(s);
}

export function decodeFile(path: string, data: Uint8Array): CapabilityFile {
  let text: string | null;
  try {
    text = new TextDecoder("utf-8", { fatal: true }).decode(data);
    if (text.includes("\u0000")) text = null;
  } catch {
    text = null;
  }
  if (text !== null) return { path, content: text, encoding: "utf8", executable: text.startsWith("#!") };
  return { path, content: toBase64(data), encoding: "base64", executable: false };
}

const dirOf = (p: string) => (p.includes("/") ? p.slice(0, p.lastIndexOf("/")) : "");
const baseName = (p: string) => p.slice(p.lastIndexOf("/") + 1);

export function skillsFromEntries(raw: RawEntry[], fallbackName: string): ImportResult {
  const entries = raw.map((e) => ({ ...e, path: normalize(e.path) })).filter((e) => e.path && !e.path.endsWith("/") && !IGNORED(e.path));
  const skipped: string[] = [];
  let roots = [...new Set(entries.filter((e) => baseName(e.path) === "SKILL.md").map((e) => dirOf(e.path)))];
  // 嵌套的 SKILL.md（例如 reference 里的示例）不算新的根：只保留最浅的
  roots = roots.filter((r) => !roots.some((o) => o !== r && (o === "" || r.startsWith(`${o}/`))));
  const missingSkillMd: string[] = [];
  if (!roots.length) {
    // 没有 SKILL.md：去掉唯一的公共顶层目录
    const tops = new Set(entries.map((e) => (e.path.includes("/") ? e.path.split("/")[0]! : "")));
    const top = tops.size === 1 ? [...tops][0]! : "";
    roots = [top];
    missingSkillMd.push(toSkillName(top || fallbackName));
  }
  const byRoot = new Map<string, CapabilityFile[]>(roots.map((r) => [r, []]));
  for (const e of entries) {
    const root = roots.filter((r) => r === "" || e.path.startsWith(`${r}/`)).sort((a, b) => b.length - a.length)[0];
    const rel = root === undefined ? null : root ? e.path.slice(root.length + 1) : e.path;
    if (rel === null || !validPath(rel)) {
      skipped.push(e.path);
      continue;
    }
    byRoot.get(root!)!.push(decodeFile(rel, e.data));
  }
  const used = new Set<string>();
  const skills: CapabilitySkill[] = [];
  for (const [root, files] of byRoot) {
    if (!files.length) continue;
    let name = toSkillName(root ? baseName(root) : fallbackName);
    for (let n = 2; used.has(name); n++) name = `${toSkillName(root ? baseName(root) : fallbackName).slice(0, 60)}-${n}`;
    used.add(name);
    files.sort((a, b) => (a.path === "SKILL.md" ? -1 : b.path === "SKILL.md" ? 1 : a.path.localeCompare(b.path)));
    skills.push({ name, files });
  }
  return { skills, skipped, missingSkillMd };
}

export function readZip(buf: Uint8Array): RawEntry[] {
  const out = unzipSync(buf);
  return Object.entries(out).map(([path, data]) => ({ path, data }));
}

/** <input type=file webkitdirectory> 或多选文件：路径取 webkitRelativePath，没有时用文件名 */
export async function readFileList(files: File[]): Promise<RawEntry[]> {
  return Promise.all(
    files.map(async (f) => ({
      path: (f as File & { webkitRelativePath?: string }).webkitRelativePath || f.name,
      data: new Uint8Array(await f.arrayBuffer()),
    })),
  );
}

/** 上传入口：单个 .zip → 解压；否则按文件夹 / 多文件处理 */
export async function importFiles(files: File[]): Promise<ImportResult> {
  if (files.length === 1 && /\.zip$/i.test(files[0]!.name)) {
    const buf = new Uint8Array(await files[0]!.arrayBuffer());
    return skillsFromEntries(readZip(buf), files[0]!.name);
  }
  const raw = await readFileList(files);
  const first = raw[0]?.path.includes("/") ? raw[0].path.split("/")[0]! : "skill";
  return skillsFromEntries(raw, first);
}
